import { useEffect, useState } from "react";
import { Twitch, Eye, Radio, ExternalLink, X } from "lucide-react";
import api from "@/lib/api";

/* LIVE ON TWITCH — who's streaming New Citizen right now. Polls the cached hub
   endpoint every 30s. Until the owner drops TWITCH_* keys into the env the strip
   renders the category CTA instead of an error. Click a card to watch inline. */

const TWITCH_PURPLE = "#9146FF";

export default function TwitchLive({ compact = false }) {
  const [data, setData] = useState(null);
  const [watch, setWatch] = useState(null); // login of the stream being watched inline

  useEffect(() => {
    let dead = false;
    const load = () => api.get("/nc/twitch/live").then((r) => { if (!dead) setData(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { dead = true; clearInterval(t); };
  }, []);

  const streams = data?.streams || [];

  return (
    <section data-testid="twitch-live" className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <span className="relative flex w-2.5 h-2.5">
          <span className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping" style={{ backgroundColor: TWITCH_PURPLE }} />
          <span className="relative inline-flex w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TWITCH_PURPLE }} />
        </span>
        <h2 className="font-bold uppercase tracking-[0.25em] text-lg sm:text-xl text-zinc-50 flex items-center gap-2.5">
          <Twitch className="w-5 h-5" style={{ color: TWITCH_PURPLE }} /> Live on Twitch
        </h2>
        <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500">
          {data ? (streams.length ? `${streams.length} streaming New Citizen` : "nobody live right now") : "checking who's live…"}
        </span>
        <a href="https://www.twitch.tv/directory/game/New%20Citizen" target="_blank" rel="noopener noreferrer"
          className="ml-auto font-mono text-[9px] uppercase tracking-[0.2em] border px-3 py-2 transition-colors flex items-center gap-1.5"
          style={{ borderColor: `${TWITCH_PURPLE}55`, color: TWITCH_PURPLE }}>
          Twitch directory <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {data && streams.length === 0 && (
        <div data-testid="twitch-empty" className="border border-dashed border-white/15 bg-white/[0.02] p-8 text-center">
          <Radio className="w-6 h-6 text-zinc-600 mx-auto mb-3" />
          <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
            {data.configured
              ? "The city is quiet on stream right now. Going live with New Citizen? You'll appear here automatically."
              : "Stream New Citizen on Twitch and you'll show up here the moment you go live."}
          </p>
          <a href="https://www.twitch.tv/directory/game/New%20Citizen" target="_blank" rel="noopener noreferrer"
            className="inline-block mt-4 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 text-black font-bold"
            style={{ backgroundColor: TWITCH_PURPLE }}>Browse the New Citizen category</a>
        </div>
      )}

      {streams.length > 0 && (
        <div className={`grid gap-4 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
          {streams.slice(0, compact ? 4 : 6).map((s) => (
            <button key={s.login} data-testid={`twitch-stream-${s.login}`} onClick={() => setWatch(s.login)}
              className="group border border-white/10 bg-black/40 text-left overflow-hidden hover:border-[#9146FF]/60 transition-colors">
              <div className="relative">
                <img src={s.thumbnail} alt="" className="w-full aspect-video object-cover opacity-90 group-hover:opacity-100 transition-opacity" loading="lazy" />
                <span className="absolute top-2 left-2 font-mono text-[8px] font-bold tracking-[0.2em] bg-red-600 text-white px-2 py-1">LIVE</span>
                <span className="absolute bottom-2 right-2 font-mono text-[9px] bg-black/70 text-zinc-200 px-2 py-1 flex items-center gap-1">
                  <Eye className="w-3 h-3" />{s.viewers.toLocaleString()}
                </span>
              </div>
              <div className="p-4">
                <p className="font-semibold text-sm text-zinc-50">{s.streamer}</p>
                <p className="font-mono text-[10px] text-zinc-400 truncate mt-0.5">{s.title}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {watch && (
        <div data-testid="twitch-player" className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setWatch(null)}>
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2">
                <Twitch className="w-4 h-4" style={{ color: TWITCH_PURPLE }} /> watching {watch}
              </p>
              <button onClick={() => setWatch(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <iframe title={`${watch} on Twitch`} allowFullScreen className="w-full aspect-video border border-white/10"
              src={`https://player.twitch.tv/?channel=${encodeURIComponent(watch)}&parent=${encodeURIComponent(window.location.hostname)}&autoplay=true&muted=true`} />
            <a href={`https://twitch.tv/${watch}`} target="_blank" rel="noopener noreferrer"
              className="inline-block mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white">
              open on twitch.tv <ExternalLink className="w-3 h-3 inline" />
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
